import type { ApplicationTaskModalAction } from '../../constants/applicationTaskModalActions';
import type { ClosingPackageModalKind } from '../../features/closingPackage/workspace/closingPackageModalKinds';
import { resolveClosingPackageModalKind } from '../../features/closingPackage/workspace/closingPackageModalKinds';
import { resolveClosingPackageCanonicalTaskType } from '../../features/closingPackage/workspace/closingPackageTaskTypes';
import { TASK_TO_PRESET } from '../../constants/taskToDocumentPreset';
import type { WorkspaceTask, WorkspaceTaskScope } from '../../store/api/workspaceApi';
import { buildRouteUploadScopeFromTask } from '../../utils/documentUploadScope';

export type ResolvedWorkspaceModal =
  | { type: 'application'; applicationId: number; action: ApplicationTaskModalAction; invoiceId?: number | null }
  | { type: 'create-invoice'; applicationId: number }
  | {
      type: 'closing-package';
      applicationId: number;
      taskId: number;
      taskType: string;
      modalKind: ClosingPackageModalKind;
    }
  | {
      type: 'application-finance-drawer';
      applicationId: number;
      obligationId?: number | null;
    }
  | {
      type: 'route-upload';
      routeId: number;
      preset?: { documentType?: string; stage?: 'loading' | 'unloading' | 'other'; fileType?: 'file' | 'photo' };
      applicationId?: number;
      groupId?: number;
      routeStopId?: number;
      taskScope?: WorkspaceTaskScope;
    }
  | { type: 'route-task-confirm'; routeId: number; taskId: number; taskType: 'CARRIER_INVOICE_CONFIRM' | 'UPD_CONFIRM' }
  | { type: 'route-finance'; routeId: number }
  | { type: 'route-documents'; routeId: number };

function buildRouteUploadModal(
  task: WorkspaceTask,
  preset: { documentType?: string; stage?: 'loading' | 'unloading' | 'other'; fileType?: 'file' | 'photo' } | undefined
): Extract<ResolvedWorkspaceModal, { type: 'route-upload' }> {
  const scope = buildRouteUploadScopeFromTask(task);
  return {
    type: 'route-upload',
    routeId: Number(task.routeId),
    preset,
    applicationId: scope?.applicationId,
    groupId: scope?.groupId,
    routeStopId: scope?.routeStopId,
    taskScope: scope?.taskScope,
  };
}

function resolveClosingPackageWorkspaceModal(
  task: WorkspaceTask
): Extract<ResolvedWorkspaceModal, { type: 'closing-package' }> | null {
  const canonical = resolveClosingPackageCanonicalTaskType(task.taskType);
  if (!canonical) return null;
  if (!task.applicationId || !task.id) return null;

  return {
    type: 'closing-package',
    applicationId: task.applicationId,
    taskId: task.id,
    taskType: String(task.taskType || '').toUpperCase(),
    modalKind: resolveClosingPackageModalKind(canonical),
  };
}

/**
 * Какой сценарий модалки открыть — только по taskType.
 * Идентификаторы (applicationId, routeId, invoiceId) лишь проверяются как обязательные поля для выбранного сценария, не влияют на выбор сценария.
 */
export function resolveWorkspaceTaskModal(task: WorkspaceTask): ResolvedWorkspaceModal | null {
  const tt = String(task.taskType || '').toUpperCase();

  const closingPackageModal = resolveClosingPackageWorkspaceModal(task);
  if (closingPackageModal) {
    return closingPackageModal;
  }

  switch (tt) {
    case 'CREATE_CLIENT_INVOICE': {
      if (!task.applicationId) return null;
      return { type: 'create-invoice', applicationId: task.applicationId };
    }
    case 'CONFIRM_INVOICE_SENT':
    case 'SEND_INVOICE_TO_CLIENT':
    case 'SEND_INVOICE': {
      if (!task.applicationId || !task.invoiceId) return null;
      return {
        type: 'application',
        applicationId: task.applicationId,
        action: 'confirm-invoice-sent',
        invoiceId: task.invoiceId,
      };
    }
    case 'CLIENT_PAYMENT_DUE': {
      if (!task.applicationId) return null;
      return {
        type: 'application-finance-drawer',
        applicationId: task.applicationId,
        obligationId: task.obligationId != null && Number.isFinite(Number(task.obligationId))
          ? Number(task.obligationId)
          : null,
      };
    }
    case 'UPLOAD_LOADING_DOCS': {
      if (!task.applicationId) break;
      return {
        type: 'application',
        applicationId: task.applicationId,
        action: 'upload-loading-docs',
        invoiceId: task.invoiceId,
      };
    }
    case 'UPLOAD_UNLOADING_DOCS': {
      if (!task.applicationId) break;
      return {
        type: 'application',
        applicationId: task.applicationId,
        action: 'upload-unloading-docs',
        invoiceId: task.invoiceId,
      };
    }
    case 'UPLOAD_UPD': {
      if (!task.applicationId) break;
      return {
        type: 'application',
        applicationId: task.applicationId,
        action: 'upload-upd',
        invoiceId: task.invoiceId,
      };
    }
    case 'CONFIRM_UPD_SENT': {
      if (!task.applicationId) return null;
      return {
        type: 'application',
        applicationId: task.applicationId,
        action: 'confirm-upd-sent',
        invoiceId: task.invoiceId,
      };
    }
    default:
      break;
  }

  if (!task.routeId) return null;
  const routeId = task.routeId;

  switch (tt) {
    case 'UPLOAD_LOADING_DOCS':
    case 'LOADING_DOCUMENTS_UPLOAD':
    case 'LOADING_PHOTO':
    case 'UPLOAD_LOADING_PHOTO':
    case 'UPLOAD_UNLOADING_DOCS':
    case 'UNLOADING_DOCUMENTS_UPLOAD':
    case 'NO_DOCS_UNLOADING':
    case 'UNLOADING_PHOTO':
    case 'UPLOAD_UPD':
    case 'UPD_UPLOAD':
    case 'TN_ORIGINAL_MISSING':
    case 'UPLOAD_TN_ORIGINAL':
    case 'CARRIER_INVOICE_MISSING':
    case 'INVOICE_EXPECTED':
      return buildRouteUploadModal(task, TASK_TO_PRESET[tt]);

    case 'CARRIER_INVOICE_CONFIRM':
    case 'UPD_CONFIRM':
      if (!task.id) return null;
      return { type: 'route-task-confirm', routeId, taskId: task.id, taskType: tt };

    case 'SEND_ORIGINAL_TN':
      return buildRouteUploadModal(task, TASK_TO_PRESET['UPLOAD_TN_ORIGINAL']);

    case 'PAYMENT_EXPECTED':
    case 'EXPENSE_CONFIRMATION':
      return { type: 'route-finance', routeId };

    case 'CLIENT_DOCS_SENT':
    case 'MISSING_CARRIER':
    case 'ADD_CARRIER':
    case 'MISSING_PRICE':
    case 'MISSING_RESPONSIBLE':
    case 'MISSING_CONTRACT':
    case 'ORIGINALS_SENT':
    case 'CLIENT_DOCS_RECEIVED':
    case 'CONFIRM_RECEIPT':
      return { type: 'route-documents', routeId };

    default:
      return null;
  }
}

/** Для кнопки задачи: можно ли открыть сценарий в модалке Workspace (без перехода на другие страницы). */
export function canOpenWorkspaceTaskModal(task: WorkspaceTask): boolean {
  return resolveWorkspaceTaskModal(task) != null;
}
